import { createContext, useContext, useMemo } from "react";
import { useAuth } from "@/features/auth";
import { useCart } from "@/features/cart/hooks/useCart";
import type { CartResponse } from "@/features/cart/types/cart.type";

type CartContextValue = {
  cart: CartResponse | undefined
  totalItems: number
  totalAmount: number
  isLoading: boolean
  isError: boolean
};

const CartContext = createContext<CartContextValue | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const { data: cart, isLoading, isError } = useCart({ enabled: isAuthenticated });

  const totalItems = useMemo(() => cart?.total_items ?? 0, [cart]);
  const totalAmount = useMemo(() => cart?.total_amount ?? 0, [cart]);

  const value = useMemo(
    () => ({
      cart,
      totalItems,
      totalAmount,
      isLoading,
      isError,
    }),
    [cart, totalItems, totalAmount, isLoading, isError],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCartContext = (): CartContextValue => {
  const context = useContext(CartContext);

  if (!context) {
    return {
      cart: undefined,
      totalItems: 0,
      totalAmount: 0,
      isLoading: false,
      isError: true,
    };
  }

  return context;
};
